import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import TermsDialog from '@/components/TermsDialog';
import { useToast } from '@/components/ui/use-toast';
import { Loader2 } from '@/icons';
import { useLanguage } from '@/contexts/LanguageContext';

const ProtectedRoute = ({ children }) => {
  const { 
    user, 
    loading, 
    onboardingStatus, 
    updateOnboardingStatus, 
    isEmailConfirmed,
    setShowTermsDialog,
    checkTermsAgreement
  } = useAuth();
  const location = useLocation();
  const { toast } = useToast();
  const { t } = useLanguage();
  
  const profileToastShownRef = React.useRef(false);
  const reportToastShownRef = React.useRef(false);

  React.useEffect(() => {
    if (!loading && user) {
      const termsAgreed = checkTermsAgreement();
      if (termsAgreed === false) { // Explicitly check for false
        setShowTermsDialog(true);
      } else {
        setShowTermsDialog(false);
      }
    }
  }, [loading, user, checkTermsAgreement, setShowTermsDialog]);


  React.useEffect(() => {
     if (location.pathname !== '/profile') profileToastShownRef.current = false;
     if (location.pathname !== '/credit-report') reportToastShownRef.current = false;
  }, [location.pathname]);


  const handleAgreeTermsInProtectedRoute = async () => {
    try {
        await updateOnboardingStatus('termsAgreed', true);
        setShowTermsDialog(false); 
        toast({ title: t("termsDialog.title", "Terms Agreed"), description: t("termsDialog.acceptButton", "Thank you for agreeing to the terms.") });
    } catch (error) {
        console.error("Failed to agree terms in ProtectedRoute:", error);
        toast({ title: t("common.error", "Error"), description: t("common.errorMessage", "Could not save terms agreement."), variant: "destructive"});
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-10rem)]">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  if (!isEmailConfirmed && location.pathname !== '/profile' && location.pathname !== '/settings') {
    // Intentionally left blank: banner component handles unconfirmed emails
  }

  if (!onboardingStatus || typeof onboardingStatus.termsAgreed === 'undefined') {
     // console.warn("ProtectedRoute: onboardingStatus or termsAgreed is undefined, treating as loading/restricted.");
     return (
       <div className="flex items-center justify-center h-[calc(100vh-10rem)]">
         <Loader2 className="h-10 w-10 animate-spin text-primary" />
         <p className="ml-2">{t('common.loading', 'Loading user status...')}</p>
       </div>
     );
  }

  if (onboardingStatus.termsAgreed === false) { // Explicitly check for false
    // The TermsDialog is now controlled by AuthContext, so ProtectedRoute doesn't need to render it.
    // It will be shown globally if needed. We just need to ensure children are rendered.
    // If strict blocking is desired here *before* terms are agreed, a redirect or specific UI can be added.
    // For now, we allow children to render, and AuthContext handles the dialog.
    // AuthContext manages the terms dialog when needed
  }


  if (!onboardingStatus.profileComplete && location.pathname !== '/profile') {
     if (!profileToastShownRef.current) {
         toast({ title: t("dashboard.completeProfilePrompt", "Complete Profile"), description: t("dashboard.completeProfilePrompt", "Please complete your profile information."), variant: "default" });
         profileToastShownRef.current = true; 
     }
     return <Navigate to="/profile" state={{ from: location }} replace />;
  }

  if (onboardingStatus.profileComplete && !onboardingStatus.creditReportLinked && location.pathname !== '/credit-report' && location.pathname !== '/profile' && location.pathname !== '/settings') {
      if (!reportToastShownRef.current) {
         toast({ title: t("creditReportPage.noDataTitle", "Link Credit Report"), description: t("creditReportPage.noDataSubtitle", "Please link or enter your credit report details."), variant: "default" });
         reportToastShownRef.current = true;
      }
     // Do not redirect; just inform the user about the missing credit report
     // return <Navigate to="/credit-report" state={{ from: location }} replace />;
  }

  return children;
};

export default ProtectedRoute;
  
